import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input, Checkbox, Button, Space, List, Spin, Select, Row, Col, Typography, Tooltip, Dropdown } from 'antd';
import { SearchOutlined, SettingOutlined, PlusOutlined, DeleteOutlined, PartitionOutlined, MoreOutlined } from '@ant-design/icons';
import { apiClient, apiErrorMessage } from '../../utils';
import moment from 'moment';
import { Condition, ConditionItem } from '../../models';

const { Text } = Typography;
const { Option } = Select;

interface AdvancedFilterCondition {
  id: string;
  operator: string;
  value: string;
  logicalOperator?: 'AND' | 'OR';
  groupId?: string;
  isGroupStart?: boolean;
  isGroupEnd?: boolean;
  groupLogicalOperator?: 'AND' | 'OR'; // Operator that applies to all conditions within this group
}

interface DistinctValue {
  label: string;
  value: string;
  isSelectAll?: boolean;
}

interface GridColumnFilterProps {
  fieldId: string;
  gridId: string;
  dataType?: string;
  selectedKeys: any[];
  setSelectedKeys: (keys: any[]) => void;
  confirm: () => void;
  clearFilters: () => void;
  onClose?: () => void;
}

// Operators aligned with global Condition/ConditionItem model
const ALL_FILTER_OPERATORS = [
  { value: '==', label: 'Eşittir' },
  { value: '!=', label: 'Eşit değildir' },
  { value: 'contains', label: 'İçerir' },
  { value: 'startsWith', label: 'İle başlar' },
  { value: 'endsWith', label: 'İle biter' },
  { value: '>', label: 'Büyüktür' },
  { value: '>=', label: 'Büyük eşittir' },
  { value: '<', label: 'Küçüktür' },
  { value: '<=', label: 'Küçük eşittir' },
  { value: 'in', label: 'İçinde' },
  { value: 'notIn', label: 'İçinde değil' },
  { value: 'isNull', label: 'Boş' },
  { value: 'isNotNull', label: 'Boş değil' },
];

// Get appropriate operators based on data type
const getOperatorsForDataType = (dataType?: string) => {
  if (!dataType) return ALL_FILTER_OPERATORS;

  // Numeric types: INTEGER, DOUBLE, DECIMAL, AUTOINC
  const numericTypes = ['INTEGER', 'DOUBLE', 'DECIMAL', 'AUTOINC'];

  // Date/DateTime types: DATE, DATETIME, TIME
  const dateTypes = ['DATE', 'DATETIME', 'TIME'];

  // Boolean type: BOOLEAN
  const booleanTypes = ['BOOLEAN'];

  if (numericTypes.includes(dataType)) {
    // Numeric types: ==, !=, >, <, >=, <=
    return ALL_FILTER_OPERATORS.filter(op => ['==', '!=', '>', '<', '>=', '<='].includes(op.value));
  } else if (dateTypes.includes(dataType)) {
    // Date/DateTime types: ==, !=, >, <, >=, <=
    return ALL_FILTER_OPERATORS.filter(op => ['==', '!=', '>', '<', '>=', '<='].includes(op.value));
  } else if (booleanTypes.includes(dataType)) {
    // Boolean types: ==, != only
    return ALL_FILTER_OPERATORS.filter(op => ['==', '!='].includes(op.value));
  }

  // Default: all operators for string types (STRING, TEXT, URL, LOOKUP, RULEBASED, PARENTROW, FOREIGNKEY, MULTIFOREIGNKEY, PICKLIST, MULTIPICKLIST, DYNAMIC)
  return ALL_FILTER_OPERATORS;
};

// Get default operator based on data type
const getDefaultOperator = (dataType?: string): string => {
  if (!dataType) return 'contains';

  // Numeric types: default to '=='
  const numericTypes = ['INTEGER', 'DOUBLE', 'DECIMAL', 'AUTOINC'];

  // Date/DateTime types: default to '=='
  const dateTypes = ['DATE', 'DATETIME', 'TIME'];

  // Boolean type: default to '=='
  const booleanTypes = ['BOOLEAN'];

  if (numericTypes.includes(dataType) || dateTypes.includes(dataType) || booleanTypes.includes(dataType)) {
    return '==';
  }

  // Default to 'contains' for string types (STRING, TEXT, URL, LOOKUP, RULEBASED, PARENTROW, FOREIGNKEY, MULTIFOREIGNKEY, PICKLIST, MULTIPICKLIST, DYNAMIC)
  return 'contains';
};

const MAX_DISTINCT_VALUES = 100;
const MAX_ADVANCED_CONDITIONS = 10;

export const GridColumnFilter: React.FC<GridColumnFilterProps> = ({
  fieldId,
  gridId,
  dataType,
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  onClose,
}) => {
  // Get appropriate operators based on data type
  const FILTER_OPERATORS = getOperatorsForDataType(dataType);

  // Helper function to format date values based on field type
  const formatDateValue = (value: string | null): string => {
    if (!value) return '';

    const isDateType = dataType === 'DATE';
    const isDateTimeType = dataType === 'DATETIME' || fieldId === 'MDM_CREATED_AT' || fieldId === 'MDM_UPDATED_AT';

    try {
      const date = moment(value);
      if (!date.isValid()) return value;

      if (isDateType) {
        return date.format('DD.MM.YYYY');
      } else if (isDateTimeType) {
        return date.format('DD.MM.YYYY HH:mm:ss');
      }
      return value;
    } catch {
      return value;
    }
  };

  const [searchText, setSearchText] = useState('');
  const [distinctValues, setDistinctValues] = useState<DistinctValue[]>([]);
  const [filteredValues, setFilteredValues] = useState<DistinctValue[]>([]);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [rootLogicalOperator, setRootLogicalOperator] = useState<'AND' | 'OR'>('AND');
  const [advancedConditions, setAdvancedConditions] = useState<AdvancedFilterCondition[]>([
    { id: '1', operator: getDefaultOperator(dataType), value: '', logicalOperator: 'AND' },
  ]);
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Convert legacy advancedConditions array into Condition tree (stable reference)
  const convertAdvancedToConditionTree = useCallback((
    adv: AdvancedFilterCondition[],
    rootLogic: 'AND' | 'OR',
    currentFieldId: string,
  ): Condition => {
    const root: Condition = { logic: rootLogic, items: [] };
    // Build group map preserving the order of items as they appear in adv
    const groupMap = new Map<string, AdvancedFilterCondition[]>();
    adv.forEach(c => {
      if (c.groupId) {
        if (!groupMap.has(c.groupId)) groupMap.set(c.groupId, []);
        groupMap.get(c.groupId)!.push(c);
      }
    });

    const processedGroups = new Set<string>();

    // Walk through adv in order and push either groups (once) or singles
    adv.forEach(ci => {
      if (ci.groupId) {
        if (processedGroups.has(ci.groupId)) return;
        processedGroups.add(ci.groupId);

        const items = groupMap.get(ci.groupId) || [];
        const groupLogic = items[0]?.groupLogicalOperator || 'AND';
        const groupCondition: Condition = { logic: groupLogic, items: [] };
        items.forEach(gci => {
          const mappedOp = mapOperatorToCondition(gci.operator);
          if (!mappedOp) {
            return;
          }
          const item: ConditionItem = {
            objectType: 'dataElement',
            objectId: currentFieldId,
            operator: mappedOp,
            // For valueless ops, keep empty string to satisfy typing
            value: '',
            includeOldRevisions: false,
          } as ConditionItem;
          if (mappedOp !== 'isNull' && mappedOp !== 'isNotNull') {
            (item as any).value = mapValueToCondition(gci.value);
          }
          groupCondition.items.push(item);
        });
        root.items.push(groupCondition);
      } else {
        const mappedOp = mapOperatorToCondition(ci.operator);
        if (!mappedOp) {
          return;
        }
        const item: ConditionItem = {
          objectType: 'dataElement',
          objectId: currentFieldId,
          operator: mappedOp,
          // For valueless ops, keep empty string to satisfy typing
          value: '',
          includeOldRevisions: false,
        } as ConditionItem;
        if (mappedOp !== 'isNull' && mappedOp !== 'isNotNull') {
          (item as any).value = mapValueToCondition(ci.value);
        }
        root.items.push(item);
      }
    });

    return root;
  }, []);

  // Convert Condition tree back to legacy advancedConditions for current UI rendering (stable reference)
  const convertConditionTreeToAdvanced = useCallback((tree: Condition): AdvancedFilterCondition[] => {
    const result: AdvancedFilterCondition[] = [];
    let groupCounter = 0;

    tree.items.forEach((child: any) => {
      if (child && 'logic' in child) {
        const group = child as Condition;
        const gid = `grp-${groupCounter++}`;
        group.items.forEach((gc: any) => {
          const item = gc as ConditionItem;
          result.push({
            id: `${Date.now()}-${Math.random()}`,
            operator: mapConditionToOperator(String(item.operator)),
            value: Array.isArray(item.value) ? String(item.value.join(',')) : String(item.value || ''),
            // For items inside a group, logicalOperator should reflect the group's logic
            logicalOperator: group.logic,
            groupId: gid,
            groupLogicalOperator: group.logic,
          });
        });
      } else {
        const item = child as ConditionItem;
        result.push({
          id: `${Date.now()}-${Math.random()}`,
          operator: mapConditionToOperator(String(item.operator)),
          value: Array.isArray(item.value) ? String(item.value.join(',')) : String(item.value || ''),
          logicalOperator: tree.logic,
        });
      }
    });

    return result.length ? result : [{ id: '1', operator: getDefaultOperator(dataType), value: '', logicalOperator: 'AND' }];
  }, [dataType]);



  // Initialize from existing selectedKeys
  useEffect(() => {
    if (selectedKeys && selectedKeys.length > 0) {
      const advancedFilter = selectedKeys[5] ? JSON.parse(selectedKeys[5]) : null;

      // For SUBGRID, search text is stored in selectedKeys[0]
      // For other types, it's in selectedKeys[7]
      const valueListSearchText = dataType === 'SUBGRID' ? selectedKeys[0] || '' : selectedKeys[7] || '';

      setSearchText(valueListSearchText); // Restore the search text used for filtering

      if (advancedFilter) {
        // If advancedFilter is legacy array, keep UI state
        if (Array.isArray(advancedFilter)) {
          setAdvancedConditions(advancedFilter);
        } else if (advancedFilter && advancedFilter.logic && advancedFilter.items) {
          // New model using Condition tree: flatten to UI representation
          try {
            setRootLogicalOperator(advancedFilter.logic);
            const legacy = convertConditionTreeToAdvanced(advancedFilter);
            setAdvancedConditions(legacy);
          } catch {}
        }
        setShowAdvanced(true);
      }
    }
  }, [selectedKeys, dataType, fieldId, convertAdvancedToConditionTree, convertConditionTreeToAdvanced]);

  const hasInitialized = useRef(false);

  // Debounce search text to prevent excessive API calls
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 600); // 600ms delay

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchText]);

  // Initial load of distinct values (triggered after search text is restored)
  // Skip for SUBGRID datatype
  useEffect(() => {
    if (!hasInitialized.current && dataType !== 'SUBGRID') {
      hasInitialized.current = true;

      const initialLoad = async () => {
        // Cancel any ongoing request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setLoading(true);
        try {
          // Use the current searchText state (which should be restored from selectedKeys[7])
          const response = await apiClient.get(
            `records/distinct-values?gridId=${gridId}&fieldId=${fieldId}&limit=${MAX_DISTINCT_VALUES}&search=${encodeURIComponent(
              searchText,
            )}`,
            { signal: abortControllerRef.current!.signal } as any,
          );

          const values = response.data?.values || [];
          const total = response.data?.totalCount || 0;

          setDistinctValues(values);
          setTotalCount(total);
          setFilteredValues(values);

          // Initialize selected values if coming from existing filter
          if (selectedKeys && selectedKeys[6]) {
            try {
              const existingSelected = JSON.parse(selectedKeys[6]);
              setSelectedValues(existingSelected);
              setSelectAll(existingSelected.length === values.length);
            } catch (error) {
              setSelectedValues([]);
              setSelectAll(false);
            }
          }
        } catch (error: any) {
          // Don't show error for aborted requests
          if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
            apiErrorMessage(error);
          }
        } finally {
          setLoading(false);
        }
      };

      initialLoad();
    }
  }, [gridId, fieldId, selectedKeys, searchText, dataType]);

  // Handle search text changes
  useEffect(() => {
    if (totalCount <= MAX_DISTINCT_VALUES) {
      // Client-side filtering when total count <= 100
      if (!debouncedSearchText) {
        setFilteredValues(distinctValues);
      } else {
        // Split by comma and trim each term
        const searchTerms = debouncedSearchText
          .split(',')
          .map(term => term.trim().toLowerCase())
          .filter(term => term !== '');

        // If no valid search terms after splitting, show all values
        if (searchTerms.length === 0) {
          setFilteredValues(distinctValues);
        } else {
          const filtered = distinctValues.filter(item => {
            const itemValue = item.value?.toString().toLowerCase() || '';
            // Check if item matches ANY of the search terms (OR logic)
            return searchTerms.some(term => itemValue.includes(term));
          });
          setFilteredValues(filtered);
        }
      }
    } else {
      // Server-side filtering when total count > 100
      if (!debouncedSearchText || debouncedSearchText.length < 2) {
        // Show initial 100 values (alphabetically ordered)
        setFilteredValues(distinctValues);
      } else {
        // Split by comma and trim each term
        const searchTerms = debouncedSearchText
          .split(',')
          .map(term => term.trim())
          .filter(term => term !== '');

        // Make server calls for each search term (when search has >= 2 characters)
        const performServerSearch = async () => {
          // Cancel any ongoing request
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          abortControllerRef.current = new AbortController();

          setLoading(true);
          try {
            // Filter out terms that are too short (< 2 chars)
            const validTerms = searchTerms.filter(term => term.length >= 2);

            if (validTerms.length === 0) {
              // No valid terms, show initial values
              setFilteredValues(distinctValues);
              setLoading(false);
              return;
            }

            // Make parallel requests for each search term
            const responses = await Promise.all(
              validTerms.map(term =>
                apiClient.get(
                  `records/distinct-values?gridId=${gridId}&fieldId=${fieldId}&limit=${MAX_DISTINCT_VALUES}&search=${encodeURIComponent(
                    term,
                  )}`,
                  { signal: abortControllerRef.current!.signal } as any,
                ),
              ),
            );

            // Combine all results and remove duplicates
            const allValues: DistinctValue[] = [];
            const seenValues = new Set<string>();

            responses.forEach(response => {
              const values = response.data?.values || [];
              values.forEach((item: DistinctValue) => {
                if (!seenValues.has(item.value)) {
                  seenValues.add(item.value);
                  allValues.push(item);
                }
              });
            });

            setFilteredValues(allValues);
          } catch (error: any) {
            // Don't show error for aborted requests
            if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
              apiErrorMessage(error);
            }
          } finally {
            setLoading(false);
          }
        };
        performServerSearch();
      }
    }
  }, [debouncedSearchText, distinctValues, totalCount, gridId, fieldId]);

  // Auto-select all filtered values when user searches, deselect when search is cleared
  useEffect(() => {
    // Skip if loading or no change in search text
    if (loading) {
      return;
    }

    // If search text is cleared (empty or only whitespace)
    if (!debouncedSearchText || debouncedSearchText.trim() === '') {
      // Deselect everything
      setSelectedValues([]);
      setSelectAll(false);
    } else {
      // If user has entered search text, auto-select all filtered values
      if (filteredValues.length > 0) {
        const allValues = filteredValues.map(item => item.value);
        setSelectedValues(allValues);
        setSelectAll(true);
      }
    }
  }, [debouncedSearchText, filteredValues, loading]);

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedValues(filteredValues.map(item => item.value));
    } else {
      setSelectedValues([]);
    }
  };

  const handleValueSelect = (value: string, checked: boolean) => {
    if (checked) {
      setSelectedValues([...selectedValues, value]);
    } else {
      setSelectedValues(selectedValues.filter(v => v !== value));
      setSelectAll(false);
    }
  };

  const handleAdvancedConditionChange = (id: string, field: string, value: any) => {
    setAdvancedConditions(conditions =>
      conditions.map(condition => {
        if (condition.id !== id) return condition;
        if (field === 'operator') {
          const nextOperator = String(value);
          const isValueless = nextOperator === 'isNull' || nextOperator === 'isNotNull';
          return {
            ...condition,
            operator: nextOperator,
            // Clear value when switching to a valueless operator
            value: isValueless ? '' : condition.value,
          };
        }
        return { ...condition, [field]: value };
      }),
    );
  };

  const addAdvancedCondition = () => {
    if (advancedConditions.length < MAX_ADVANCED_CONDITIONS) {
      setAdvancedConditions([
        ...advancedConditions,
        {
          id: Date.now().toString(),
          operator: getDefaultOperator(dataType),
          value: '',
          logicalOperator: rootLogicalOperator,
          isGroupStart: false,
          isGroupEnd: false,
          groupId: undefined,
        },
      ]);
    }
  };

  const addConditionToGroup = (groupId: string) => {
    if (advancedConditions.length < MAX_ADVANCED_CONDITIONS) {
      // Find the last condition in the group and get the group's logical operator
      let lastGroupConditionIndex = -1;
      let groupLogicalOperator: 'AND' | 'OR' = 'AND';

      for (let i = advancedConditions.length - 1; i >= 0; i--) {
        if (advancedConditions[i].groupId === groupId) {
          lastGroupConditionIndex = i;
          groupLogicalOperator = advancedConditions[i].groupLogicalOperator || 'AND';
          break;
        }
      }

      if (lastGroupConditionIndex === -1) return;

      const newCondition = {
        id: Date.now().toString(),
        operator: getDefaultOperator(dataType),
        value: '',
        logicalOperator: groupLogicalOperator,
        isGroupStart: false,
        isGroupEnd: false,
        groupId: groupId,
        groupLogicalOperator: groupLogicalOperator,
      };

      setAdvancedConditions(conditions => {
        const newConditions = [...conditions];
        // Insert the new condition right after the last group condition
        newConditions.splice(lastGroupConditionIndex + 1, 0, newCondition);
        return newConditions;
      });
    }
  };

  const removeAdvancedCondition = (id: string) => {
    if (advancedConditions.length > 1) {
      setAdvancedConditions(conditions => conditions.filter(c => c.id !== id));
    }
  };

  const createGroupFromCondition = (conditionId: string) => {
    const groupId = Date.now().toString();

    setAdvancedConditions(conditions =>
      conditions.map(condition =>
        condition.id === conditionId ? { ...condition, isGroupStart: true, groupId, groupLogicalOperator: 'AND' } : condition,
      ),
    );
  };

  const removeConditionFromGroup = (conditionId: string) => {
    setAdvancedConditions(conditions => {
      const condition = conditions.find(c => c.id === conditionId);
      if (!condition?.groupId) return conditions;

      const groupConditions = conditions.filter(c => c.groupId === condition.groupId);

      // If this is the last condition in the group, remove the entire group
      if (groupConditions.length <= 1) {
        return conditions.map(c =>
          c.groupId === condition.groupId ? { ...c, groupId: undefined, isGroupStart: false, isGroupEnd: false } : c,
        );
      }

      // Otherwise just remove this condition
      return conditions.filter(c => c.id !== conditionId);
    });
  };

  const ungroupConditions = (groupId: string) => {
    setAdvancedConditions(conditions =>
      conditions.map(c =>
        c.groupId === groupId
          ? {
              ...c,
              groupId: undefined,
              isGroupStart: false,
              isGroupEnd: false,
              logicalOperator: rootLogicalOperator,
              groupLogicalOperator: undefined,
            }
          : c,
      ),
    );
  };

  const deleteGroup = (groupId: string) => {
    setAdvancedConditions(conditions => conditions.filter(c => c.groupId !== groupId));
  };

  const updateGroupLogicalOperator = (groupId: string, operator: 'AND' | 'OR') => {
    setAdvancedConditions(conditions =>
      conditions.map(c => (c.groupId === groupId ? { ...c, groupLogicalOperator: operator, logicalOperator: operator } : c)),
    );
  };

  const updateRootLogicalOperator = (operator: 'AND' | 'OR') => {
    setRootLogicalOperator(operator);
    // Update all root-level conditions (those without groupId)
    setAdvancedConditions(conditions => conditions.map(c => (!c.groupId ? { ...c, logicalOperator: operator } : c)));
  };

  // Helper function to group conditions for rendering
  const getGroupedConditions = () => {
    const result: Array<{ type: 'single' | 'group'; conditions: AdvancedFilterCondition[]; groupId?: string }> = [];
    let i = 0;

    while (i < advancedConditions.length) {
      const condition = advancedConditions[i];

      if (condition.groupId) {
        // This is part of a group
        const groupConditions = advancedConditions.filter(c => c.groupId === condition.groupId);
        result.push({
          type: 'group',
          conditions: groupConditions,
          groupId: condition.groupId,
        });
        // Skip all conditions in this group
        i = advancedConditions.findIndex(c => c === groupConditions[groupConditions.length - 1]) + 1;
      } else {
        // This is a single condition
        result.push({
          type: 'single',
          conditions: [condition],
        });
        i++;
      }
    }

    return result;
  };
  const handleApplyFilter = () => {
    // Prevent filter submission when loading
    if (loading) {
      return;
    }

    // Filter out empty conditions but keep valueless operators like isNull/isNotNull
    const validAdvancedConditions = advancedConditions.filter(c => {
      const mapped = mapOperatorToCondition(c.operator);
      if (!mapped) return false;
      if (mapped === 'isNull' || mapped === 'isNotNull') return true;
      return c.value != null && c.value.toString().trim() !== '';
    });
    const hasAdvancedConditions = showAdvanced && validAdvancedConditions.length > 0;
    const hasSelectedValues = selectedValues.length > 0;
    const hasStatusFilters = selectedKeys[1] === '1' || selectedKeys[2] === '1' || selectedKeys[3] === '1' || selectedKeys[4] === '1';
    const hasSearchText = searchText && searchText.trim() !== '';

    if (isSubGrid) {
      // For SUBGRID, use search text as text filter
      if (!hasSearchText && !hasStatusFilters) {
        // No filter applied - clear everything
        clearFilters();
        if (onClose) onClose();
        return;
      }

      const newSelectedKeys = [
        hasSearchText ? searchText : '', // [0] - text filter (used for SUBGRID search)
        selectedKeys[1] || '0', // [1] - null filter
        selectedKeys[2] || '0', // [2] - not null filter
        selectedKeys[3] || '0', // [3] - task filter
        selectedKeys[4] || '0', // [4] - error filter
        '', // [5] - advanced conditions (not used for SUBGRID)
        '', // [6] - selected distinct values (not used for SUBGRID)
        '', // [7] - value list search text (not used for SUBGRID)
      ];

      setSelectedKeys(newSelectedKeys);
      confirm();
      if (onClose) onClose();
      return;
    }

    if (!hasSelectedValues && !hasAdvancedConditions && !hasStatusFilters) {
      // No filter applied - clear everything like opening grid for first time
      clearFilters();
      if (onClose) onClose();
      return;
    }

    const newSelectedKeys = [
      '', // [0] - text filter (not used when using distinct values)
      selectedKeys[1] || '0', // [1] - null filter
      selectedKeys[2] || '0', // [2] - not null filter
      selectedKeys[3] || '0', // [3] - task filter
      selectedKeys[4] || '0', // [4] - error filter
      hasAdvancedConditions ? JSON.stringify(convertAdvancedToConditionTree(validAdvancedConditions, rootLogicalOperator, fieldId)) : '', // [5] - advanced conditions as Condition tree
      hasSelectedValues ? JSON.stringify(selectedValues) : '', // [6] - selected distinct values
      searchText || '', // [7] - value list search text
    ];

    setSelectedKeys(newSelectedKeys);
    confirm();
    if (onClose) onClose();
  };

  const handleClearFilter = () => {
    setSearchText('');
    setSelectedValues([]);
    setSelectAll(false);
    setAdvancedConditions([
      {
        id: '1',
        operator: getDefaultOperator(dataType),
        value: '',
        logicalOperator: undefined,
        isGroupStart: false,
        isGroupEnd: false,
        groupId: undefined,
      },
    ]);
    setShowAdvanced(false);
    clearFilters();
    if (onClose) onClose();
  };

  const toggleAdvancedMode = (useAdvanced: boolean) => {
    setShowAdvanced(useAdvanced);
    if (useAdvanced) {
      // Clear basic search when switching to advanced
      setSearchText('');
      setSelectedValues([]);
      setSelectAll(false);
    } else {
      // Clear advanced conditions when switching to basic
      setAdvancedConditions([
        {
          id: '1',
          operator: getDefaultOperator(dataType),
          value: '',
          logicalOperator: undefined,
          isGroupStart: false,
          isGroupEnd: false,
          groupId: undefined,
        },
      ]);
    }
  };


  function mapOperatorToCondition(op: string): ConditionItem['operator'] | undefined {
    // Accept only global tokens exactly as defined in ConditionItem
    const allowedOps: ConditionItem['operator'][] = [
      '==',
      '!=',
      '>',
      '>=',
      '<',
      '<=',
      'contains',
      'startsWith',
      'endsWith',
      'in',
      'notIn',
      'isNull',
      'isNotNull',
      'errors',
      'tasks',
      'myTasks',
      'search',
      'activeStatus',
      'processStatus',
      'revisions',
      'lastUpdatedAfter',
      'lastUpdatedBefore',
      'recordIds',
      'parentRecordIds',
      'revisionsFor',
    ];
    return (allowedOps as string[]).includes(op) ? (op as ConditionItem['operator']) : undefined;
  }

  function mapConditionToOperator(op: string): string {
    // Identity for global tokens; no fallbacks
    return op;
  }

  function mapValueToCondition(val: any): any {
    if (typeof val !== 'string') return val;
    // Convert comma-separated lists into arrays for in/notIn
    const trimmed = val.trim();
    if (trimmed.indexOf(',') >= 0) {
      return trimmed.split(',').map(v => v.trim()).filter(v => v !== '');
    }
    return trimmed;
  }

  const isSubGrid = dataType === 'SUBGRID';

  return (
    <div style={{ padding: 16, width: 420 }}>
      {!isSubGrid && showAdvanced ? (
        /* Advanced Search Mode */
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text strong style={{ fontSize: 14 }}>
              Gelişmiş arama
            </Text>
            <Button type="link" onClick={() => toggleAdvancedMode(false)} style={{ padding: 0, fontSize: 12 }}>
              ← Basit aramaya dön
            </Button>
          </div>

          <div
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              backgroundColor: '#fafafa',
              marginBottom: 16,
            }}
          >
            {/* Root Logical Operator Header - only show if multiple items */}
            <div
              style={{
                borderBottom: '1px solid #d9d9d9',
                padding: '8px 12px',
                backgroundColor: '#f0f0f0',
                borderRadius: '6px 6px 0 0',
                marginBottom: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>
                <span
                  onClick={() => updateRootLogicalOperator('AND')}
                  style={{
                    cursor: 'pointer',
                    color: rootLogicalOperator === 'AND' ? '#000' : '#999',
                    fontWeight: rootLogicalOperator === 'AND' ? 'bold' : 'normal',
                  }}
                >
                  ve
                </span>
                <span style={{ margin: '0 6px', color: '#d9d9d9' }}>|</span>
                <span
                  onClick={() => updateRootLogicalOperator('OR')}
                  style={{
                    cursor: 'pointer',
                    color: rootLogicalOperator === 'OR' ? '#000' : '#999',
                    fontWeight: rootLogicalOperator === 'OR' ? 'bold' : 'normal',
                  }}
                >
                  veya
                </span>
              </div>
            </div>
            <div style={{ padding: getGroupedConditions().length > 1 ? '0 16px 16px 16px' : '16px' }}>
              {getGroupedConditions().map((item, itemIndex) => (
                <div key={item.type === 'group' ? `group-${item.groupId}` : `single-${item.conditions[0].id}`}>
                  {item.type === 'group' ? (
                    /* Group Container with Label */
                    <Row gutter={4} align="middle" style={{ marginBottom: 12 }}>
                      {getGroupedConditions().length > 1 && (
                        <Col span={3}>
                          <div style={{ color: '#999', fontSize: '12px', fontWeight: 'normal', textAlign: 'center' }}>
                            {itemIndex > 0 ? (rootLogicalOperator === 'AND' ? 've' : 'veya') : ''}
                          </div>
                        </Col>
                      )}
                      <Col span={getGroupedConditions().length > 1 ? 21 : 24}>
                        <div
                          style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: 4,
                            marginBottom: 4,
                            backgroundColor: '#fafafa',
                          }}
                        >
                          {/* Group Header */}
                          <div
                            style={{
                              borderBottom: '1px solid #d9d9d9',
                              padding: '8px 12px',
                              backgroundColor: '#f0f0f0',
                              borderRadius: '4px 4px 0 0',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>
                              <span
                                onClick={() => updateGroupLogicalOperator(item.groupId!, 'AND')}
                                style={{
                                  cursor: 'pointer',
                                  color: (item.conditions[0]?.groupLogicalOperator || 'AND') === 'AND' ? '#000' : '#999',
                                  fontWeight: (item.conditions[0]?.groupLogicalOperator || 'AND') === 'AND' ? 'bold' : 'normal',
                                }}
                              >
                                ve
                              </span>
                              <span style={{ margin: '0 6px', color: '#d9d9d9' }}>|</span>
                              <span
                                onClick={() => updateGroupLogicalOperator(item.groupId!, 'OR')}
                                style={{
                                  cursor: 'pointer',
                                  color: (item.conditions[0]?.groupLogicalOperator || 'AND') === 'OR' ? '#000' : '#999',
                                  fontWeight: (item.conditions[0]?.groupLogicalOperator || 'AND') === 'OR' ? 'bold' : 'normal',
                                }}
                              >
                                veya
                              </span>
                            </div>
                            <div style={{ marginLeft: 'auto' }}>
                              <Dropdown
                                overlay={
                                  <div style={{ background: 'white', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: 4 }}>
                                    <Button
                                      type="text"
                                      size="small"
                                      onClick={() => ungroupConditions(item.groupId!)}
                                      style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 2 }}
                                    >
                                      Gruplama Kaldır
                                    </Button>
                                    <Button
                                      type="text"
                                      size="small"
                                      danger
                                      onClick={() => deleteGroup(item.groupId!)}
                                      style={{ display: 'block', width: '100%', textAlign: 'left' }}
                                    >
                                      Grubu Sil
                                    </Button>
                                  </div>
                                }
                                trigger={['click']}
                              >
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<MoreOutlined />}
                                  style={{ fontSize: 10, padding: '0 4px', color: '#999' }}
                                />
                              </Dropdown>
                            </div>
                          </div>

                          {/* Group Content */}
                          <div style={{ padding: item.conditions.length > 1 ? '12px 12px' : '12px' }}>
                            {item.conditions.map((condition, conditionIndex) => (
                              <div key={condition.id} style={{ marginBottom: 12 }}>
                                <Row gutter={4} align="middle">
                                  {(item.conditions.length > 1 || getGroupedConditions().length > 1) && (
                                    <Col span={3}>
                                      <div style={{ color: '#999', fontSize: '12px', fontWeight: 'normal', textAlign: 'center' }}>
                                        {conditionIndex > 0 ? (condition.groupLogicalOperator === 'AND' ? 've' : 'veya') : ''}
                                      </div>
                                    </Col>
                                  )}
                                  <Col span={item.conditions.length > 1 || getGroupedConditions().length > 1 ? 8 : 10}>
                                    <Select
                                      size="small"
                                      value={condition.operator}
                                      onChange={value => handleAdvancedConditionChange(condition.id, 'operator', value)}
                                      style={{ width: '100%' }}
                                    >
                                      {FILTER_OPERATORS.map(op => (
                                        <Option key={op.value} value={op.value}>
                                          {op.label}
                                        </Option>
                                      ))}
                                    </Select>
                                  </Col>
                                  <Col span={item.conditions.length > 1 || getGroupedConditions().length > 1 ? 10 : 10}>
                                    <Input
                                      size="small"
                                      placeholder="Değer"
                                      value={condition.value}
                                      onChange={e => handleAdvancedConditionChange(condition.id, 'value', e.target.value)}
                                      disabled={condition.operator === 'isNull' || condition.operator === 'isNotNull' || loading}
                                      onPressEnter={handleApplyFilter}
                                    />
                                  </Col>
                                  <Col span={item.conditions.length > 1 || getGroupedConditions().length > 1 ? 3 : 4}>
                                    <Tooltip title="Gruptan Çıkar">
                                      <Button
                                        type="text"
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        onClick={() => removeConditionFromGroup(condition.id)}
                                        danger
                                        style={{ fontSize: 10, padding: '0 4px' }}
                                      />
                                    </Tooltip>
                                  </Col>
                                </Row>
                              </div>
                            ))}

                            <Button
                              type="link"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() => addConditionToGroup(item.groupId!)}
                              style={{ padding: 0, height: 'auto', marginTop: 8, textAlign: 'left', fontSize: '12px' }}
                            >
                              Koşul ekle
                            </Button>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  ) : (
                    /* Single Condition */
                    <div style={{ marginBottom: 12 }}>
                      <Row gutter={4} align="middle">
                        {getGroupedConditions().length > 1 && (
                          <Col span={3}>
                            <div style={{ color: '#999', fontSize: '12px', fontWeight: 'normal', textAlign: 'center' }}>
                              {itemIndex > 0 ? (rootLogicalOperator === 'AND' ? 've' : 'veya') : ''}
                            </div>
                          </Col>
                        )}
                        <Col span={getGroupedConditions().length > 1 ? 8 : 10}>
                          <Select
                            size="small"
                            value={item.conditions[0].operator}
                            onChange={value => handleAdvancedConditionChange(item.conditions[0].id, 'operator', value)}
                            style={{ width: '100%' }}
                          >
                            {FILTER_OPERATORS.map(op => (
                              <Option key={op.value} value={op.value}>
                                {op.label}
                              </Option>
                            ))}
                          </Select>
                        </Col>
                        <Col span={getGroupedConditions().length > 1 ? 9 : 8}>
                          <Input
                            size="small"
                            placeholder="Değer"
                            value={item.conditions[0].value}
                            onChange={e => handleAdvancedConditionChange(item.conditions[0].id, 'value', e.target.value)}
                            disabled={item.conditions[0].operator === 'isNull' || item.conditions[0].operator === 'isNotNull'}
                          />
                        </Col>
                        <Col span={getGroupedConditions().length > 1 ? 4 : 6} style={{ display: 'flex', gap: 2 }}>
                          <Tooltip title="Bu koşuldan grup oluştur">
                            <Button
                              type="text"
                              size="small"
                              icon={<PartitionOutlined />}
                              onClick={() => createGroupFromCondition(item.conditions[0].id)}
                              style={{ fontSize: 10, padding: '0 4px' }}
                            />
                          </Tooltip>
                          {advancedConditions.length > 1 && (
                            <Tooltip title="Koşulu Sil">
                              <Button
                                type="text"
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={() => removeAdvancedCondition(item.conditions[0].id)}
                                danger
                                style={{ fontSize: 10, padding: '0 4px' }}
                              />
                            </Tooltip>
                          )}
                        </Col>
                      </Row>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {advancedConditions.length < MAX_ADVANCED_CONDITIONS && (
              <div style={{ padding: '0 16px 16px 16px' }}>
                <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={addAdvancedCondition}
                  style={{ padding: 0, height: 'auto', textAlign: 'left', fontSize: '12px' }}
                >
                  Koşul ekle
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Default Search Mode */
        <div>
          {isSubGrid ? (
            /* Simplified view for SUBGRID - only search input */
            <div
              style={{
                border: '1px solid #e8e8e8',
                borderRadius: 6,
                padding: 12,
                marginBottom: 16,
              }}
            >
              <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Arama
              </Text>
              <Input
                placeholder="Ara..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onPressEnter={handleApplyFilter}
                prefix={<SearchOutlined />}
                allowClear
                size="small"
                disabled={loading}
              />
            </div>
          ) : (
            /* Normal view - Distinct Values List with integrated text search */
            <div
              style={{
                border: '1px solid #e8e8e8',
                borderRadius: 6,
                padding: 12,
                marginBottom: 16,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ fontSize: 12 }}>
                  Değer seçimi
                </Text>
              </div>

              {/* Text Search integrated into value selection */}
              <Input
                placeholder="Değerleri filtrele..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onPressEnter={handleApplyFilter}
                prefix={<SearchOutlined />}
                suffix={loading ? <Spin size="small" style={{ marginRight: 8 }} /> : null}
                allowClear
                size="small"
                style={{ marginBottom: 8 }}
              />

              <div
                style={{
                  maxHeight: 180,
                  overflow: 'auto',
                  border: '1px solid #f0f0f0',
                  borderRadius: 4,
                  backgroundColor: '#fafafa',
                }}
              >
                {loading ? (
                  <div style={{ padding: 20, textAlign: 'center' }}>
                    <Spin size="small" />
                  </div>
                ) : (
                  <List
                    size="small"
                    dataSource={[
                      ...(filteredValues.length > 0
                        ? [
                            {
                              label: `Tümü (${filteredValues.length}${totalCount > MAX_DISTINCT_VALUES ? ` / ${totalCount}` : ''})`,
                              value: '__select_all__',
                              isSelectAll: true,
                            },
                          ]
                        : []),
                      ...filteredValues,
                    ]}
                    renderItem={value => (
                      <List.Item style={{ padding: '4px 8px', borderBottom: 'none', display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          indeterminate={value.isSelectAll && selectedValues.length > 0 && selectedValues.length < filteredValues.length}
                          checked={value.isSelectAll ? selectAll : selectedValues.includes(value.value)}
                          onChange={e =>
                            value.isSelectAll ? handleSelectAll(e.target.checked) : handleValueSelect(value.value, e.target.checked)
                          }
                          style={{ fontSize: 12, display: 'flex', alignItems: 'center' }}
                        >
                          <span
                            style={{
                              display: 'inline-block',
                              maxWidth: 180,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              lineHeight: '1.2',
                            }}
                          >
                            {value.label === null || value.label === undefined || value.label === '' ? (
                              <Text type="secondary">(Boş)</Text>
                            ) : (
                              formatDateValue(value.label) || value.label
                            )}
                          </span>
                        </Checkbox>
                      </List.Item>
                    )}
                  />
                )}
              </div>

              {totalCount > MAX_DISTINCT_VALUES && (
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                  {debouncedSearchText && debouncedSearchText.length >= 2 ? (
                    `* Arama sonuçları (${filteredValues.length} / ${totalCount})`
                  ) : (
                    <>
                      <span>
                        * İlk {MAX_DISTINCT_VALUES} değer gösteriliyor (Toplam {totalCount}).
                      </span>
                      <br />
                      <span>* Arama yapmak için en az 2 karakter girin.</span>
                    </>
                  )}
                </Text>
              )}

              {/* Advanced Search Button at bottom of value list */}
              <div style={{ marginTop: 12, textAlign: 'right' }}>
                <Button type="link" icon={<SettingOutlined />} onClick={() => toggleAdvancedMode(true)} style={{ fontSize: 12 }}>
                  Gelişmiş arama
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status Filters - Always visible, distinct container */}
      <div
        style={{
          border: '1px solid #e8e8e8',
          borderRadius: 6,
          padding: 12,
          marginBottom: 16,
        }}
      >
        <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Durum filtreleri
        </Text>

        <Row gutter={[8, 4]}>
          <Col span={12}>
            <Checkbox
              checked={selectedKeys[1] === '1'}
              onChange={e => {
                const currentKeys = [...selectedKeys];
                currentKeys[1] = e.target.checked ? '1' : '0';
                setSelectedKeys(currentKeys);
              }}
            >
              <Text style={{ fontSize: 11 }}>Boş olanlar</Text>
            </Checkbox>
          </Col>
          <Col span={12}>
            <Checkbox
              checked={selectedKeys[2] === '1'}
              onChange={e => {
                const currentKeys = [...selectedKeys];
                currentKeys[2] = e.target.checked ? '1' : '0';
                setSelectedKeys(currentKeys);
              }}
            >
              <Text style={{ fontSize: 11 }}>Boş olmayanlar</Text>
            </Checkbox>
          </Col>
          <Col span={12}>
            <Checkbox
              checked={selectedKeys[3] === '1'}
              onChange={e => {
                const currentKeys = [...selectedKeys];
                currentKeys[3] = e.target.checked ? '1' : '0';
                setSelectedKeys(currentKeys);
              }}
            >
              <Text style={{ fontSize: 11 }}>Görev olanlar</Text>
            </Checkbox>
          </Col>
          <Col span={12}>
            <Checkbox
              checked={selectedKeys[4] === '1'}
              onChange={e => {
                const currentKeys = [...selectedKeys];
                currentKeys[4] = e.target.checked ? '1' : '0';
                setSelectedKeys(currentKeys);
              }}
            >
              <Text style={{ fontSize: 11 }}>Hata olanlar</Text>
            </Checkbox>
          </Col>
        </Row>
      </div>

      {/* Action Buttons */}
      <div
        style={{
          borderTop: '1px solid #f0f0f0',
          paddingTop: 12,
        }}
      >
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button type="primary" icon={<SearchOutlined />} size="small" onClick={handleApplyFilter} disabled={loading}>
            Filtrele
          </Button>
          <Button size="small" onClick={handleClearFilter}>
            Temizle
          </Button>
        </Space>
      </div>
    </div>
  );
};
